import { useEffect, useState } from "react";
import type { SchrijvenTopic, TopicSection } from "../../data/schrijven-study/topics/types";
import { reapplyTranslations } from "@a2/i18n/course-helpers";
import { currentLocale } from "../../lib/i18n";
import { TopicRenderer } from "../study/TopicRenderer";

// ---------------------------------------------------------------------------
// Catalog shape — mirrors the JSON schema written by translate-courses.ts
// ---------------------------------------------------------------------------

interface SectionCatalogEntry {
  kind: string;
  level?: number;
  text?: Record<number, string>;
  calloutTitle?: Record<number, string>;
  items?: Record<number, Record<number, string>>;
  headers?: Record<number, Record<number, string>>;
  rows?: Record<number, Record<number, Record<number, string>>>;
}

interface CourseCatalog {
  schemaVersion: number;
  topic: string;
  title: string;
  tagline: string;
  summary: string;
  sections: SectionCatalogEntry[];
}

// ---------------------------------------------------------------------------
// Hook — dynamic-import catalog for current locale; returns null on miss
// ---------------------------------------------------------------------------

function useCourseTranslations(locale: string, slug: string): CourseCatalog | null {
  const [catalog, setCatalog] = useState<CourseCatalog | null>(null);

  useEffect(() => {
    if (locale === "en") {
      setCatalog(null);
      return;
    }

    let cancelled = false;

    // Why: dynamic import path uses a template literal so Vite/bundler can
    // enumerate the possible JSON files at build time. If the file doesn't
    // exist yet (Task 5.5 hasn't run), the import rejects → fallback to EN.
    import(`@a2/i18n/course-translations/${locale}/${slug}.json`)
      .then((mod: unknown) => {
        if (!cancelled) {
          const data = (mod as { default?: CourseCatalog }).default ?? (mod as CourseCatalog);
          setCatalog(data);
        }
      })
      .catch(() => {
        // Catalog not generated yet — fall back to EN source silently.
        if (!cancelled) setCatalog(null);
      });

    return () => {
      cancelled = true;
    };
  }, [locale, slug]);

  return catalog;
}

// ---------------------------------------------------------------------------
// Section merging — rewrites text fields using the catalog entry
// ---------------------------------------------------------------------------

function mergeSection(
  section: TopicSection,
  t: SectionCatalogEntry | undefined,
): TopicSection {
  if (!t) return section;

  switch (section.kind) {
    case "heading":
      return {
        ...section,
        text: t.text ? reapplyTranslations(section.text, t.text) : section.text,
      };
    case "paragraph":
      return {
        ...section,
        text: t.text ? reapplyTranslations(section.text, t.text) : section.text,
      };
    case "rule":
      return {
        ...section,
        text: t.text ? reapplyTranslations(section.text, t.text) : section.text,
      };
    case "callout":
      return {
        ...section,
        text: t.text ? reapplyTranslations(section.text, t.text) : section.text,
        title:
          section.title != null && t.calloutTitle
            ? reapplyTranslations(section.title, t.calloutTitle)
            : section.title,
      };
    case "list":
      return {
        ...section,
        items: section.items.map((item, idx) => {
          const itemT = t.items?.[idx];
          return itemT ? reapplyTranslations(item, itemT) : item;
        }),
      };
    case "table":
      return {
        ...section,
        headers: section.headers.map((h, idx) => {
          const hT = t.headers?.[idx];
          return hT ? reapplyTranslations(h, hT) : h;
        }),
        rows: section.rows.map((row, rIdx) => {
          const rowT = t.rows?.[rIdx];
          return row.map((cell, cIdx) => {
            const cellT = rowT?.[cIdx];
            return cellT ? reapplyTranslations(cell, cellT) : cell;
          });
        }),
      };
    case "examples":
      return {
        ...section,
        rows: section.rows.map((r) => ({ ...r })),
      };
    case "formula":
      return {
        ...section,
        title: t.text && section.title != null
          ? reapplyTranslations(section.title, t.text)
          : section.title,
      };
    default:
      return section;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Thin adapter — the underlying TopicRenderer was authored against the
 * Spreken Topic type which carries extra picker fields (relevantDeelen,
 * relevantCriteria). Schrijven topics don't need those, so we pad with
 * empty arrays at the render boundary instead of bloating SchrijvenTopic.
 *
 * When a locale translation catalog exists, sections are re-woven with
 * translated plain-string segments before rendering. DutchPhrase markers
 * in the source are preserved verbatim regardless of locale.
 *
 * If TopicRenderer ever stops needing those fields, this adapter goes
 * away and SchrijvenTopicPage can call TopicRenderer directly.
 */
export function SchrijvenTopicRenderer({ topic }: { topic: SchrijvenTopic }) {
  const locale = currentLocale();
  const catalog = useCourseTranslations(locale, topic.slug);

  const translatedTopic = catalog
    ? {
        ...topic,
        title: catalog.title,
        tagline: catalog.tagline,
        summary: catalog.summary,
        sections: topic.sections.map((section, idx) =>
          mergeSection(section, catalog.sections[idx]),
        ),
      }
    : topic;

  return (
    <TopicRenderer
      topic={{
        slug: translatedTopic.slug,
        title: translatedTopic.title,
        tagline: translatedTopic.tagline,
        duration: translatedTopic.duration,
        summary: translatedTopic.summary,
        relevantDeelen: [],
        relevantCriteria: [],
        sections: translatedTopic.sections,
      }}
    />
  );
}
